import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { ContactData } from '@/types/api';

/** Write payload for a contact — phones/emails are labeled channel arrays. */
export type ContactPayload = {
  name?: string;
  title?: string;
  kind?: string;
  phones?: { label?: string; number: string; is_primary?: boolean }[];
  emails?: { label?: string; address: string; is_primary?: boolean }[];
  is_primary?: boolean;
  receives_billing?: boolean;
  receives_approvals?: boolean;
  receives_technical?: boolean;
  notes?: string;
};

const KEY = (clientId: number) => ['contacts', clientId];

export function useContacts(clientId: number | undefined) {
  return useQuery({
    queryKey: clientId == null ? ['contacts', null] : KEY(clientId),
    enabled: clientId != null,
    queryFn: async (): Promise<ContactData[]> => {
      const { data } = await api.get<ContactData[]>(`/v1/clients/${clientId}/contacts`);
      return data;
    },
  });
}

export function useCreateContact(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ContactPayload): Promise<ContactData> => {
      await primeCsrf();
      const { data } = await api.post<ContactData>(`/v1/clients/${clientId}/contacts`, input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(clientId) }),
  });
}

export function useUpdateContact(clientId: number, contactId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: ContactPayload): Promise<ContactData> => {
      await primeCsrf();
      const { data } = await api.patch<ContactData>(
        `/v1/clients/${clientId}/contacts/${contactId}`,
        patch,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(clientId) }),
  });
}

/**
 * Attach an address to an existing contact. The API replaces a contact's whole
 * email list on write, so their current addresses are sent back alongside the
 * new one, primary flags intact.
 */
export function useAddContactEmail(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      contact,
      address,
      label = 'work',
    }: {
      contact: ContactData;
      address: string;
      label?: string;
    }): Promise<ContactData> => {
      await primeCsrf();
      const { data } = await api.patch<ContactData>(
        `/v1/clients/${clientId}/contacts/${contact.id}`,
        {
          emails: [
            ...contact.emails.map((e) => ({
              label: e.label,
              address: e.address,
              is_primary: e.is_primary,
            })),
            { label, address, is_primary: false },
          ],
        },
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(clientId) }),
  });
}

export function useDeleteContact(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (contactId: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/clients/${clientId}/contacts/${contactId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(clientId) }),
  });
}
